import { initializeApp } from 'https://www.gstatic.com/firebasejs/10.7.1/firebase-app.js';
import { getAuth, onAuthStateChanged, signOut } from 'https://www.gstatic.com/firebasejs/10.7.1/firebase-auth.js';

const firebaseConfig = {
  apiKey: "AIzaSyDmxltNbfQYZiUe9tG8IkUUD4DWsE5ozrY",
  authDomain: "lagenda-1047a.firebaseapp.com",
  projectId: "lagenda-1047a",
  storageBucket: "lagenda-1047a.firebasestorage.app",
  messagingSenderId: "618799057702",
  appId: "1:618799057702:web:6615fe93cd876fa35d0fe7",
  measurementId: "G-M7PKWL2YMY"
};

const app = initializeApp(firebaseConfig);
const auth = getAuth(app);

const listeners = new Set();
// Envoi analytics tolérant à l'ordre de chargement : perf.js rejoue la file
// window.__trackQueue quand il arrive, puis la remplace par un passe-plat.
function track(name, params) {
  try {
    if (typeof window.trackEvent === 'function') window.trackEvent(name, params || {});
    else (window.__trackQueue = window.__trackQueue || []).push([name, params || {}]);
  } catch (_) {}
}

let currentUser = null;
let readyResolve;
const readyPromise = new Promise(r => { readyResolve = r; });

// ── État data (synchronisé depuis Firestore, cache localStorage) ─────
let savedIds = new Set();
let followedSlugs = new Set();
let currentTheme = null;   // 'jour' | 'nuit' | null (jamais set)
let dataReady = false;     // true après le premier snapshot Firestore

// Firestore chargé à la demande (évite ~60kB sur pages anonymes)
let fs = null;             // { db, doc, onSnapshot, setDoc, arrayUnion, arrayRemove, serverTimestamp, getDoc }
let snapshotUnsub = null;

function savesKey(uid){ return `lagenda_saves_${uid}`; }
function followsKey(uid){ return `lagenda_follows_${uid}`; }
function themeKey(uid){ return `lagenda_theme_${uid}`; }
function migratedKey(uid){ return `lagenda_fs_migrated_${uid}`; }
function lastSeenKey(uid){ return `lagenda_lastseen_${uid}`; }
const ANON_THEME_KEY = 'lagenda_theme_anon';

function readSetLS(key){
  try { const raw = localStorage.getItem(key); return new Set(raw ? JSON.parse(raw) : []); }
  catch { return new Set(); }
}
function writeSetLS(key, set){
  try { localStorage.setItem(key, JSON.stringify([...set])); } catch {}
}

function hydrateFromCache(uid){
  savedIds = readSetLS(savesKey(uid));
  followedSlugs = readSetLS(followsKey(uid));
  const t = localStorage.getItem(themeKey(uid));
  if (t === 'jour' || t === 'nuit') currentTheme = t;
}

async function loadFirestore(){
  if (fs) return fs;
  const mod = await import('https://www.gstatic.com/firebasejs/10.7.1/firebase-firestore.js');
  fs = {
    db: mod.getFirestore(app),
    doc: mod.doc,
    onSnapshot: mod.onSnapshot,
    setDoc: mod.setDoc,
    getDoc: mod.getDoc,
    arrayUnion: mod.arrayUnion,
    arrayRemove: mod.arrayRemove,
    serverTimestamp: mod.serverTimestamp,
  };
  return fs;
}

// ── Heartbeat "derniere visite" ─────────────────────────────────────
// Firebase Auth ne met a jour lastSignInTime qu'a une VRAIE authentification.
// Un user avec session persistante peut revenir tous les jours sans que ce
// champ bouge. On pose donc un last_seen_at sur le doc Firestore a chaque
// visite, throttle a 1 ecriture / 30 min pour ne pas cramer le quota.
const LAST_SEEN_THROTTLE_MS = 30 * 60 * 1000;

async function touchLastSeen(user){
  try {
    const prev = Number(localStorage.getItem(lastSeenKey(user.uid)) || 0);
    if (Date.now() - prev < LAST_SEEN_THROTTLE_MS) return;
  } catch {}

  const F = await loadFirestore();
  try {
    await F.setDoc(F.doc(F.db, 'users', user.uid),
      { last_seen_at: F.serverTimestamp() }, { merge: true });
    try { localStorage.setItem(lastSeenKey(user.uid), String(Date.now())); } catch {}
  } catch (e) {
    console.error('[LAgendaAuth] last_seen_at failed:', e);
  }
}

async function startSync(user){
  hydrateFromCache(user.uid);
  emitDataChange();

  const F = await loadFirestore();
  const userDoc = F.doc(F.db, 'users', user.uid);

  // Migration one-shot : pousse vers Firestore UNIQUEMENT les items locaux
  // absents du remote, via arrayUnion (race-safe vs autre device).
  if (!localStorage.getItem(migratedKey(user.uid))){
    try {
      const snap = await F.getDoc(userDoc);
      const remote = snap.exists() ? snap.data() : {};
      const remoteSaves = new Set(Array.isArray(remote.saves) ? remote.saves : []);
      const remoteFollows = new Set(Array.isArray(remote.follows) ? remote.follows : []);
      const newSaves = [...savedIds].filter(id => !remoteSaves.has(id));
      const newFollows = [...followedSlugs].filter(s => !remoteFollows.has(s));
      const themeToMigrate = (!remote.theme && currentTheme) ? currentTheme : null;

      if (newSaves.length || newFollows.length || themeToMigrate){
        const payload = { updatedAt: F.serverTimestamp() };
        if (newSaves.length) payload.saves = F.arrayUnion(...newSaves);
        if (newFollows.length) payload.follows = F.arrayUnion(...newFollows);
        if (themeToMigrate) payload.theme = themeToMigrate;
        await F.setDoc(userDoc, payload, { merge: true });
      }
      localStorage.setItem(migratedKey(user.uid), '1');
    } catch (e) {
      console.error('[LAgendaAuth] migration failed:', e);
      // flag non posé : retry prochaine session
    }
  }

  // Sync temps-réel cross-device
  snapshotUnsub = F.onSnapshot(userDoc, (snap) => {
    const data = snap.exists() ? snap.data() : {};
    savedIds = new Set(Array.isArray(data.saves) ? data.saves : []);
    followedSlugs = new Set(Array.isArray(data.follows) ? data.follows : []);
    if (data.theme === 'jour' || data.theme === 'nuit') currentTheme = data.theme;
    // localStorage devient le cache (rehydratation rapide au prochain boot)
    writeSetLS(savesKey(user.uid), savedIds);
    writeSetLS(followsKey(user.uid), followedSlugs);
    if (currentTheme) { try { localStorage.setItem(themeKey(user.uid), currentTheme); } catch {} }
    dataReady = true;
    emitDataChange();
  }, (err) => {
    console.error('[LAgendaAuth] snapshot error:', err);
  });
}

function stopSync(){
  if (snapshotUnsub) { try { snapshotUnsub(); } catch {} }
  snapshotUnsub = null;
  savedIds = new Set();
  followedSlugs = new Set();
  currentTheme = null;
  dataReady = false;
}

function emitDataChange(){
  try {
    window.dispatchEvent(new CustomEvent('lagenda-user-data-change', {
      detail: { savedIds: new Set(savedIds), followedSlugs: new Set(followedSlugs), theme: currentTheme }
    }));
  } catch {}
}

// Écriture Firestore fire-and-forget (optimistic côté UI)
async function writeArrayOp(fieldName, op, value){
  if (!currentUser || !fs) return;
  const userDoc = fs.doc(fs.db, 'users', currentUser.uid);
  try {
    await fs.setDoc(userDoc, {
      [fieldName]: op === 'union' ? fs.arrayUnion(value) : fs.arrayRemove(value),
      updatedAt: fs.serverTimestamp(),
    }, { merge: true });
  } catch (e) {
    console.error(`[LAgendaAuth] ${op} ${fieldName} failed:`, e);
  }
}
async function writeField(fieldName, value){
  if (!currentUser || !fs) return;
  const userDoc = fs.doc(fs.db, 'users', currentUser.uid);
  try {
    await fs.setDoc(userDoc, {
      [fieldName]: value,
      updatedAt: fs.serverTimestamp(),
    }, { merge: true });
  } catch (e) {
    console.error(`[LAgendaAuth] write ${fieldName} failed:`, e);
  }
}

function firstName(user){
  if (!user) return '';
  const dn = (user.displayName || '').trim();
  if (dn) return dn.split(/\s+/)[0];
  if (user.email) return user.email.split('@')[0];
  return '';
}

function updateHeaderUI(){
  document.querySelectorAll('.btn-inscr').forEach(btn => {
    if (currentUser){
      const name = firstName(currentUser);
      btn.textContent = name ? `♦ ${name}` : '♦ Mon compte';
      btn.setAttribute('data-auth', 'logged-in');
      btn.setAttribute('title', 'Mon compte');
      btn.href = '/compte';
      btn.onclick = null;
    } else {
      btn.textContent = "S'inscrire";
      btn.setAttribute('data-auth', 'guest');
      btn.removeAttribute('title');
      btn.href = '/inscription';
      btn.onclick = null;
    }
  });
}

async function updateAdminNav(){
  const adminLinks = document.querySelectorAll('[data-admin-only]');
  if (!adminLinks.length) return;
  let isAdmin = false;
  if (currentUser){
    try {
      const t = await currentUser.getIdTokenResult();
      isAdmin = (t.claims.role || 'user') === 'admin';
    } catch (e) { console.error('[LAgendaAuth] updateAdminNav role check failed:', e); }
  }
  adminLinks.forEach(el => { el.style.display = isAdmin ? '' : 'none'; });
}

onAuthStateChanged(auth, (user) => {
  const prevUid = currentUser ? currentUser.uid : null;
  const nextUid = user ? user.uid : null;
  if (prevUid !== nextUid) stopSync();

  currentUser = user || null;
  updateHeaderUI();
  updateAdminNav();
  listeners.forEach(fn => { try { fn(currentUser); } catch (e) { console.error(e); } });
  if (readyResolve){ readyResolve(currentUser); readyResolve = null; }

  if (user) { startSync(user); touchLastSeen(user); }
});

document.addEventListener('DOMContentLoaded', () => { updateHeaderUI(); updateAdminNav(); });

const LAgendaAuth = {
  getUser(){ return currentUser; },
  ready(){ return readyPromise; },
  onChange(cb){ listeners.add(cb); if (currentUser !== undefined) try { cb(currentUser); } catch {} return () => listeners.delete(cb); },
  async logout(){ try { await signOut(auth); } catch (e) { console.error(e); } },

  // Role custom claim (admin, producer, prod_plus, user). Force refresh pour
  // recuperer un changement cote Cloud Function sans attendre l'expiration token.
  async getRole(){
    if (!currentUser) return null;
    try {
      const t = await currentUser.getIdTokenResult();
      return t.claims.role || 'user';
    } catch (e) {
      console.error('[LAgendaAuth] getRole failed:', e);
      return 'user';
    }
  },

  requireAuth(returnTo, reason){
    if (currentUser) return true;
    const target = returnTo || (location.pathname + location.search + location.hash);
    track('auth_prompt', { reason: reason || 'unknown', page: location.pathname });
    location.href = `/inscription?mode=signup&returnTo=${encodeURIComponent(target)}`;
    return false;
  },

  // ── Saves (concerts enregistrés) ─────────────────────────
  getSavedIds(){
    if (!currentUser) return new Set();
    return new Set(savedIds);
  },
  isSaved(id){
    if (!id || !currentUser) return false;
    return savedIds.has(id);
  },
  toggleSave(id){
    if (!id) return { ok:false };
    if (!currentUser){ this.requireAuth(undefined, 'save'); return { ok:false, redirected:true }; }
    const was = savedIds.has(id);
    if (was) savedIds.delete(id); else savedIds.add(id);
    track(was ? 'favorite_remove' : 'favorite_add', { concert_id: id });
    writeSetLS(savesKey(currentUser.uid), savedIds);
    writeArrayOp('saves', was ? 'remove' : 'union', id);
    emitDataChange();
    return { ok:true, saved: !was };
  },

  // ── Follows (artistes suivis) ────────────────────────────
  getFollowedArtists(){
    if (!currentUser) return new Set();
    return new Set(followedSlugs);
  },
  isFollowed(artistSlug){
    if (!artistSlug || !currentUser) return false;
    return followedSlugs.has(artistSlug);
  },
  toggleFollow(artistSlug){
    if (!artistSlug) return { ok:false };
    if (!currentUser){ this.requireAuth(undefined, 'follow'); return { ok:false, redirected:true }; }
    const was = followedSlugs.has(artistSlug);
    if (was) followedSlugs.delete(artistSlug); else followedSlugs.add(artistSlug);
    track(was ? 'artist_unfollow' : 'artist_follow', { artist_slug: artistSlug });
    writeSetLS(followsKey(currentUser.uid), followedSlugs);
    writeArrayOp('follows', was ? 'remove' : 'union', artistSlug);
    emitDataChange();
    return { ok:true, followed: !was };
  },

  // ── Theme (jour | nuit) ──────────────────────────────────
  getTheme(){
    if (currentTheme) return currentTheme;
    if (currentUser){
      const t = localStorage.getItem(themeKey(currentUser.uid));
      if (t === 'jour' || t === 'nuit') return t;
    }
    const anon = localStorage.getItem(ANON_THEME_KEY);
    return (anon === 'jour' || anon === 'nuit') ? anon : null;
  },
  setTheme(theme){
    if (theme !== 'jour' && theme !== 'nuit') return { ok:false };
    currentTheme = theme;
    if (currentUser){
      try { localStorage.setItem(themeKey(currentUser.uid), theme); } catch {}
      writeField('theme', theme);
    } else {
      try { localStorage.setItem(ANON_THEME_KEY, theme); } catch {}
    }
    emitDataChange();
    return { ok:true, theme };
  },

  // ── Sync status + event bus pour UI réactive ─────────────
  isDataReady(){ return dataReady; },
  onDataChange(cb){
    const handler = (e) => { try { cb(e.detail); } catch (err) { console.error(err); } };
    window.addEventListener('lagenda-user-data-change', handler);
    return () => window.removeEventListener('lagenda-user-data-change', handler);
  }
};

window.LAgendaAuth = LAgendaAuth;
window.dispatchEvent(new CustomEvent('lagenda-auth-ready'));
